import { useMemo, useState, useEffect } from 'react';
import { FormGroup } from 'src/components/ui';
import DatePicker from 'react-datepicker';
import dayjs from 'dayjs';
import { useLingui } from '@lingui/react';
import { t } from '@lingui/core/macro';
import { useDebouncedEffect } from '../../hooks/useDebounceEffect';
import { useJnx } from '../../util/jnx';
import { useSideChannelSubscription } from '../../util/useSideChannel';
import getPathFromId from '../../util/getPathFromId';
import { WidgetProps } from '@rjsf/utils';
import useWrapInput from './utils/useWrapInput';
import { enUS, es } from 'date-fns/locale';
import useTestId from '../../hooks/useTestId';
import { useAutofillSafeName } from '../../hooks/useAutofillSafeName';

import 'react-datepicker/dist/react-datepicker.css';
import Icon from 'src/components/Icon';

// --- Locale helpers ---

const DATE_FORMATS = {
  en: 'PP',
  es: "dd 'de' MMM, yyyy",
} as const;

export function getDateFnsLocale(linguiLocale: string) {
  return linguiLocale.startsWith('es') ? es : enUS;
}

export function getDateFormat(linguiLocale: string): string {
  return linguiLocale.startsWith('es') ? DATE_FORMATS.es : DATE_FORMATS.en;
}

/** Converts a yyyy-mm-dd ISO string to a Date object, or null if empty/invalid. */
export function isoToDate(iso: string | undefined): Date | null {
  if (!iso) return null;
  const d = dayjs(iso);
  return d.isValid() ? d.toDate() : null;
}

/** Converts a Date object to a yyyy-mm-dd ISO string. */
export function dateToISO(date: Date): string {
  return dayjs(date).format('YYYY-MM-DD');
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// --- Component ---

export default function DateWidget(props: WidgetProps) {
  const {
    id,
    required,
    disabled,
    readonly,
    autofocus,
    registry,
    schema,
    uiSchema: {
      'ui:classNames': classNames,
      'ui:requiredIf': uiRequiredIf,
      'ui:disableIf': uiDisableIf,
      'ui:prefix': prefix,
      'ui:sufix': sufix,
      'ui:tooltipText': tooltipText = '',
      'ui:disablePastDates': disablePastDates = false,
      'ui:disableFutureDates': disableFutureDates = false,
      'ui:disablePastDatesFrom': disablePastDatesFrom,
      'ui:disableFutureDatesFrom': disableFutureDatesFrom,
      'ui:onChange': onChangeExpr,
      'ui:staticText': staticText = false,
    },
    value,
    onChange: onChangeForm,
  } = props;
  const formContext = registry?.formContext;

  const { i18n } = useLingui();
  const dateFormat = getDateFormat(i18n.locale);
  const locale = getDateFnsLocale(i18n.locale);

  const [current, setCurrent] = useState<Date | null>(() => isoToDate(value));
  const testIdProps = useTestId(id);

  const rootFormData = useSideChannelSubscription(formContext.sideChannel, 0);
  const { setFormDataValues } = formContext;
  const functionBinds = useMemo(() => ({ set: setFormDataValues }), [setFormDataValues]);
  const onChangeJnx = useJnx(onChangeExpr, { functionBinds });

  // Sync when upstream value changes (e.g. form reset)
  useEffect(() => {
    setCurrent(isoToDate(value));
  }, [value]);

  useDebouncedEffect(
    () => {
      const iso = current ? dateToISO(current) : undefined;
      if (iso !== value) {
        onChangeForm(iso);
        if (onChangeExpr && iso) {
          setTimeout(() => {
            onChangeJnx.evalAsync(onChangeExpr, '', {
              newValue: iso,
              rootFormData,
            });
          }, 100);
        }
      }
    },
    [current],
    450
  );

  const portalId = `datepicker-portal-${id}`;
  useEffect(() => {
    const elem = document.createElement('div');
    elem.id = portalId;
    document.body.appendChild(elem);
    return () => {
      document.body.removeChild(elem);
    };
  }, [portalId]);

  const dataPath = useMemo(() => getPathFromId(id), [id]);
  const dataValue = value || '';

  const autofillSafeName = useAutofillSafeName();

  const requiredIfJnx = useJnx(uiRequiredIf);
  const requiredIf = useMemo(
    () =>
      requiredIfJnx &&
      requiredIfJnx.eval(rootFormData || {}, dataPath, {
        root: rootFormData,
        formContext,
      }),
    [dataValue, rootFormData, formContext]
  );

  const disableIfJnx = useJnx(uiDisableIf);
  const disableIf = useMemo(
    () =>
      disableIfJnx &&
      disableIfJnx.eval(rootFormData || {}, dataPath, {
        rootData: rootFormData,
        formContext,
      }),
    [dataValue, rootFormData, formContext]
  );

  const disablePastDatesFromJnx = useJnx(ISO_DATE_RE.test(disablePastDatesFrom) ? undefined : disablePastDatesFrom);
  const resolvedPastDatesFrom = useMemo(() => {
    if (ISO_DATE_RE.test(disablePastDatesFrom)) return disablePastDatesFrom;
    if (disablePastDatesFromJnx) return disablePastDatesFromJnx.eval(rootFormData || {}, dataPath, { rootData: rootFormData, formContext });
  }, [dataValue, rootFormData, formContext, disablePastDatesFrom]);

  const disableFutureDatesFromJnx = useJnx(ISO_DATE_RE.test(disableFutureDatesFrom) ? undefined : disableFutureDatesFrom);
  const resolvedFutureDatesFrom = useMemo(() => {
    if (ISO_DATE_RE.test(disableFutureDatesFrom)) return disableFutureDatesFrom;
    if (disableFutureDatesFromJnx) return disableFutureDatesFromJnx.eval(rootFormData || {}, dataPath, { rootData: rootFormData, formContext });
  }, [dataValue, rootFormData, formContext, disableFutureDatesFrom]);

  const today = dayjs()
    .startOf('day')
    .toDate();

  const minDate = useMemo(() => {
    if (resolvedPastDatesFrom) return isoToDate(resolvedPastDatesFrom) ?? today;
    if (disablePastDates) return today;
  }, [disablePastDates, resolvedPastDatesFrom]);

  const maxDate = useMemo(() => {
    if (resolvedFutureDatesFrom) return isoToDate(resolvedFutureDatesFrom) ?? today;
    if (disableFutureDates) return today;
  }, [disableFutureDates, resolvedFutureDatesFrom]);

  if (staticText) {
    return (
      <div id={id} {...testIdProps} className="mb-3" style={{ height: '40px' }}>
        {current
          ? dayjs(current)
              .locale(i18n.locale)
              .format('LL')
          : '---'}
      </div>
    );
  }

  return (
    <FormGroup className="input-wrapper">
      {useWrapInput({
        input: (
          <DatePicker
            id={id}
            name={autofillSafeName}
            autoComplete="off"
            {...testIdProps}
            locale={locale}
            dateFormat={dateFormat}
            selected={current}
            onChange={(date: Date | null) => setCurrent(date)}
            disabled={!!(disabled || disableIf)}
            readOnly={!!readonly}
            autoFocus={!!autofocus}
            required={!!(required || requiredIf)}
            minDate={minDate}
            maxDate={maxDate}
            placeholderText={t`Select a date`}
            className={`form-control custom ${classNames || ''} pl-8 w-full`}
            wrapperClassName="block"
            showPopperArrow={false}
            showIcon
            icon={<Icon icon="Calendar" className="fa-regular h-full" style={{ zIndex: 99 }} />}
            portalId={portalId}
            popperProps={{
              strategy: 'fixed',
            }}
            popperPlacement="bottom-start"
            toggleCalendarOnIconClick
            showYearDropdown
            scrollableYearDropdown
          />
        ),
        prefix,
        sufix,
        tooltipText,
        rootFormData,
        dataPath,
        fieldId: id,
        formContext,
        formData: current ? dateToISO(current) : value,
        fieldSchema: schema,
      })}
    </FormGroup>
  );
}
